
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowUpRight, 
  ChevronRight, 
  MessageSquare, 
  Plus, 
  Route, 
  Users 
} from 'lucide-react';
import { FundingCollaborator } from '@/types/gigs';

// Mock data for collaborators
const mockCollaborators: FundingCollaborator[] = [
  {
    id: '1',
    name: 'Alex Rivera',
    genre: ['Indie Rock', 'Alternative'],
    location: 'Portland, OR',
    audienceSize: 12500,
    compatibility: 92,
    fundingGoal: 15000,
    fundingRaised: 8750,
    projectType: 'tour',
    project: 'West Coast Tour Summer 2024',
    email: 'alex@example.com',
    image: 'https://source.unsplash.com/random/400x400/?musician-1'
  },
  {
    id: '2',
    name: 'Melanie Chen',
    genre: ['Electronic', 'Pop'],
    location: 'Los Angeles, CA',
    audienceSize: 18200,
    compatibility: 86,
    fundingGoal: 25000,
    fundingRaised: 12800,
    projectType: 'album',
    project: 'Debut Album Production',
    email: 'melanie@example.com',
    image: 'https://source.unsplash.com/random/400x400/?musician-2'
  },
  {
    id: '3',
    name: 'Marcus James',
    genre: ['Hip-Hop', 'R&B'],
    location: 'Atlanta, GA',
    audienceSize: 20500,
    compatibility: 78,
    fundingGoal: 12000,
    fundingRaised: 7200,
    projectType: 'video',
    project: 'Music Video Series',
    email: 'marcus@example.com',
    image: 'https://source.unsplash.com/random/400x400/?musician-3'
  },
  {
    id: '4',
    name: 'Sofia Rodriguez',
    genre: ['Latin', 'Pop'],
    location: 'Miami, FL',
    audienceSize: 15800,
    compatibility: 88,
    fundingGoal: 18000,
    fundingRaised: 10500,
    projectType: 'tour',
    project: 'East Coast Tour Fall 2023',
    email: 'sofia@example.com',
    image: 'https://source.unsplash.com/random/400x400/?musician-4'
  },
  {
    id: '5',
    name: 'Liam Wilson',
    genre: ['Folk', 'Acoustic'],
    location: 'Nashville, TN',
    audienceSize: 9200,
    compatibility: 95,
    fundingGoal: 8000,
    fundingRaised: 6400,
    projectType: 'album',
    project: 'Acoustic EP Recording',
    email: 'liam@example.com',
    image: 'https://source.unsplash.com/random/400x400/?musician-5'
  }
];

// Mock data for active pools
const mockActivePools = [
  {
    id: '1',
    name: 'Midwest Tour Collab',
    members: 3,
    goal: 12000,
    raised: 7800,
    type: 'tour',
    deadline: '2023-12-10',
    genres: ['Indie', 'Folk', 'Rock']
  },
  {
    id: '2',
    name: 'Studio Session Bundle',
    members: 4,
    goal: 9000,
    raised: 5400,
    type: 'recording',
    deadline: '2023-11-30',
    genres: ['Electronic', 'Hip-Hop']
  }
];

const CollaborativeFundingPools: React.FC = () => {
  const [activeTab, setActiveTab] = useState('explore');
  const [selectedCollaborator, setSelectedCollaborator] = useState<FundingCollaborator | null>(null);
  
  return (
    <div className="p-6">
      <Tabs defaultValue="explore" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full md:w-auto">
          <TabsTrigger value="explore">Explore Collaborators</TabsTrigger>
          <TabsTrigger value="active">Active Pools</TabsTrigger>
          <TabsTrigger value="create">Create Pool</TabsTrigger>
        </TabsList>
        
        <TabsContent value="explore" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Find Compatible Collaborators</h2>
              <p className="text-muted-foreground">
                Join forces with compatible artists to achieve shared funding goals
              </p>
            </div>
            <div className="flex gap-3">
              <Select defaultValue="compatibility">
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compatibility">Compatibility</SelectItem>
                  <SelectItem value="location">Location</SelectItem>
                  <SelectItem value="audienceSize">Audience Size</SelectItem>
                  <SelectItem value="fundingGoal">Funding Goal</SelectItem>
                </SelectContent>
              </Select>
              <Button>
                <Plus className="h-4 w-4 mr-2" /> Add Filters
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!selectedCollaborator ? (
              <>
                {mockCollaborators.map((collab) => (
                  <Card key={collab.id} className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedCollaborator(collab)}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-16 w-16 rounded-full">
                          <AvatarImage src={collab.image} alt={collab.name} />
                          <AvatarFallback>{collab.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg">{collab.name}</h3>
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
                              {collab.compatibility}% Match
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {collab.location} • {collab.audienceSize.toLocaleString()} followers
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {collab.genre.map((g, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{g}</Badge>
                            ))}
                          </div>
                          
                          <div className="mt-4">
                            <div className="font-medium text-sm">{collab.projectType === 'tour' ? 'Tour Funding' : collab.projectType === 'album' ? 'Album Funding' : 'Video Funding'}</div>
                            <div className="flex justify-between text-xs mt-1 mb-1">
                              <span>{collab.project}</span>
                              <span>
                                ${collab.fundingRaised.toLocaleString()} / ${collab.fundingGoal.toLocaleString()}
                              </span>
                            </div>
                            <Progress value={(collab.fundingRaised / collab.fundingGoal) * 100} className="h-1.5" />
                          </div>
                          
                          <div className="flex justify-between items-center mt-4">
                            <Button variant="outline" size="sm" className="text-xs px-2">
                              <MessageSquare className="h-3 w-3 mr-1" /> Message
                            </Button>
                            <div className="flex items-center text-sm text-primary">
                              View Details <ChevronRight className="h-4 w-4" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : (
              <Card className="border shadow-sm col-span-1 md:col-span-2">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedCollaborator(null)}>
                      ← Back to list
                    </Button>
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
                      {selectedCollaborator.compatibility}% Match
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/3">
                      <div className="flex flex-col items-center text-center">
                        <Avatar className="h-32 w-32 rounded-full">
                          <AvatarImage src={selectedCollaborator.image} alt={selectedCollaborator.name} />
                          <AvatarFallback>{selectedCollaborator.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <h2 className="text-2xl font-bold mt-4">{selectedCollaborator.name}</h2>
                        <p className="text-muted-foreground">{selectedCollaborator.location}</p>
                        <div className="flex flex-wrap justify-center gap-1 mt-2">
                          {selectedCollaborator.genre.map((g, i) => (
                            <Badge key={i} variant="outline">{g}</Badge>
                          ))}
                        </div>
                        
                        <div className="flex justify-between w-full mt-6 text-center">
                          <div>
                            <div className="font-bold text-xl">{selectedCollaborator.audienceSize.toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground">Audience</div>
                          </div>
                          <div>
                            <div className="font-bold text-xl">180</div>
                            <div className="text-xs text-muted-foreground">Shows</div>
                          </div>
                          <div>
                            <div className="font-bold text-xl">3</div>
                            <div className="text-xs text-muted-foreground">Releases</div>
                          </div>
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                          <Button>
                            Propose Collaboration
                          </Button>
                          <Button variant="outline">
                            <MessageSquare className="h-4 w-4 mr-2" /> Message
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="md:w-2/3">
                      <h3 className="text-xl font-semibold mb-4">Funding Project</h3>
                      <Card className="border shadow-sm mb-6">
                        <CardContent className="p-6">
                          <h4 className="font-medium text-lg">{selectedCollaborator.project}</h4>
                          <div className="flex justify-between text-sm mt-2 mb-2">
                            <span className="text-muted-foreground">Funding Progress</span>
                            <span className="font-medium">
                              ${selectedCollaborator.fundingRaised.toLocaleString()} of ${selectedCollaborator.fundingGoal.toLocaleString()}
                            </span>
                          </div>
                          <Progress value={(selectedCollaborator.fundingRaised / selectedCollaborator.fundingGoal) * 100} className="h-2 mb-4" />
                          <div className="text-sm text-muted-foreground mb-4">
                            {selectedCollaborator.projectType === 'tour' 
                              ? 'Seeking collaborative funding for a comprehensive tour, including venue bookings, transportation, accommodation, and promotional expenses.'
                              : selectedCollaborator.projectType === 'album'
                              ? 'Looking to pool resources for album production, including studio time, mixing, mastering, and initial promotional campaign.'
                              : 'Seeking partners to co-fund a series of music videos with shared production resources and cross-promotion.'
                            }
                          </div>
                          <Button className="w-full">
                            Join Funding Pool
                          </Button>
                        </CardContent>
                      </Card>
                      
                      <h3 className="text-xl font-semibold mb-4">Compatibility Analysis</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <Card className="border shadow-sm bg-muted/50">
                          <CardContent className="p-4">
                            <h4 className="text-sm font-medium mb-2">Audience Overlap</h4>
                            <div className="text-2xl font-bold mb-1">68%</div>
                            <p className="text-xs text-muted-foreground">
                              Strong demographic similarity in core listeners
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="border shadow-sm bg-muted/50">
                          <CardContent className="p-4">
                            <h4 className="text-sm font-medium mb-2">Geographic Synergy</h4>
                            <div className="text-2xl font-bold mb-1">85%</div>
                            <p className="text-xs text-muted-foreground">
                              Strong audience presence in same regions
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="border shadow-sm bg-muted/50">
                          <CardContent className="p-4">
                            <h4 className="text-sm font-medium mb-2">Career Stage</h4>
                            <div className="text-2xl font-bold mb-1">92%</div>
                            <p className="text-xs text-muted-foreground">
                              Very similar trajectory and current momentum
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="border shadow-sm bg-muted/50">
                          <CardContent className="p-4">
                            <h4 className="text-sm font-medium mb-2">Project Alignment</h4>
                            <div className="text-2xl font-bold mb-1">76%</div>
                            <p className="text-xs text-muted-foreground">
                              Goals and timelines are highly compatible
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-4">Recommended Collaboration Models</h3>
                      <div className="space-y-3">
                        <Card className="border shadow-sm hover:border-primary cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-blue-100 p-2 rounded-full">
                                <Route className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <h4 className="font-medium">Joint Tour Package</h4>
                                <p className="text-sm text-muted-foreground">
                                  Co-headline shows with shared production costs
                                </p>
                              </div>
                              <ArrowUpRight className="h-4 w-4 ml-auto text-primary" />
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="border shadow-sm hover:border-primary cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-purple-100 p-2 rounded-full">
                                <Users className="h-5 w-5 text-purple-600" />
                              </div>
                              <div>
                                <h4 className="font-medium">Audience Cross-Promotion</h4>
                                <p className="text-sm text-muted-foreground">
                                  Structured marketing exchange with defined KPIs
                                </p>
                              </div>
                              <ArrowUpRight className="h-4 w-4 ml-auto text-primary" />
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="active" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold">Active Funding Pools</h2>
            <p className="text-muted-foreground">
              Collaborative funding initiatives you're currently part of
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mockActivePools.map((pool) => (
              <Card key={pool.id} className="border shadow-sm">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{pool.name}</h3>
                      <div className="text-sm text-muted-foreground">
                        {pool.members} collaborators • {pool.type === 'tour' ? 'Tour Funding' : 'Recording Funding'}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Manage
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pool.genres.map((genre, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{genre}</Badge>
                    ))}
                  </div>
                  
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mt-2 mb-1">
                      <span>Funding Progress</span>
                      <span className="font-medium">
                        ${pool.raised.toLocaleString()} / ${pool.goal.toLocaleString()}
                      </span>
                    </div>
                    <Progress value={(pool.raised / pool.goal) * 100} className="h-2" />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-muted-foreground">
                        {Math.round((pool.raised / pool.goal) * 100)}% funded
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Deadline: {new Date(pool.deadline).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex -space-x-3 mt-4">
                    {[...Array(4)].map((_, i) => (
                      <Avatar key={i} className="border-2 border-background w-8 h-8">
                        <AvatarImage src={`https://source.unsplash.com/random/100x100/?face-${i}`} />
                        <AvatarFallback>U{i}</AvatarFallback>
                      </Avatar>
                    ))}
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-xs font-medium">
                      +{pool.members - 3}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <Button className="w-full" size="sm">Fund</Button>
                    <Button variant="outline" className="w-full" size="sm">Share</Button>
                    <Button variant="outline" className="w-full" size="sm">Chat</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <Card className="border border-dashed flex flex-col items-center justify-center p-6 h-full">
              <div className="text-center">
                <div className="bg-muted text-primary w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Plus className="h-6 w-6" />
                </div>
                <h3 className="font-medium mb-2">Start New Funding Pool</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Combine resources with compatible artists
                </p>
                <Button onClick={() => setActiveTab('create')}>
                  Create Pool
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="create" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold">Create Funding Pool</h2>
            <p className="text-muted-foreground">
              Start a collaborative funding initiative with other artists
            </p>
          </div>
          
          <Card className="border shadow-sm">
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Pool Name</label>
                  <Input className="mt-1" placeholder="Give your funding pool a name" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Project Type</label>
                    <Select>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tour">Tour Funding</SelectItem>
                        <SelectItem value="album">Album/EP Recording</SelectItem>
                        <SelectItem value="video">Music Video</SelectItem>
                        <SelectItem value="marketing">Marketing Campaign</SelectItem>
                        <SelectItem value="merch">Merchandise Production</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Funding Goal</label>
                    <Input className="mt-1" type="number" placeholder="Enter amount" />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Start Date</label>
                    <Input className="mt-1" type="date" />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">End Date</label>
                    <Input className="mt-1" type="date" />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input className="mt-1" placeholder="Describe your funding pool and its goals" />
                </div>
                
                <div>
                  <label className="text-sm font-medium">Who can join?</label>
                  <Select defaultValue="invite">
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select access type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invite">Invite only</SelectItem>
                      <SelectItem value="open">Open to all</SelectItem>
                      <SelectItem value="approval">Request to join (with approval)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Invite Compatible Artists</h3>
                <div className="bg-muted/50 rounded-md p-4 mb-4">
                  <div className="text-sm text-muted-foreground mb-2">
                    AI-Recommended Collaborators for this project
                  </div>
                  <div className="space-y-2">
                    {mockCollaborators.slice(0, 3).map((collab) => (
                      <div key={collab.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={collab.image} alt={collab.name} />
                            <AvatarFallback>{collab.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-sm">{collab.name}</div>
                            <div className="text-xs text-muted-foreground">{collab.location}</div>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {collab.compatibility}% Match
                        </Badge>
                        <Button variant="outline" size="sm">
                          Invite
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="pt-4 flex justify-end gap-3">
                <Button variant="outline">
                  Save Draft
                </Button>
                <Button>
                  Create Funding Pool
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CollaborativeFundingPools;
